/**
 * The crew and equipment sections of an earthworks job's own page (EW-1).
 * Each is a full-width panel; an add form opens inline in the panel (a row is
 * a child of the job, not a record of its own), and a removal asks in-app
 * first. Copied from Pipeline & Facility's JobSections.
 *
 * Writes are dispatch roles. A closed job (completed / cancelled) takes no
 * writes — the API's 409 job_closed — so its forms are not offered. A refusal
 * that still arrives reads as one sentence from `ewRefusal`, never a green toast.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddEwCrew, useAddEwJobEquipment, useRemoveEwCrew, useRemoveEwJobEquipment } from '../../hooks/useEarthworks';
import { blankToNull, ewDay, ewRefusal } from '../../lib/earthworks';
import type { EwCatalog, EwCrew, EwJobDetail, EwJobEquipment } from '../../types/earthworks';
import {
  EW_DATE_INPUT,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwPanel,
  EwRegister,
  type EwColumn,
} from './EwBits';

const SETTLED = { isLoading: false };

/** One in-app confirm for a removal; the refusal (if any) shows inside it. */
function RemoveConfirm({
  open,
  title,
  body,
  confirmLabel,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <EwDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={EW_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={EW_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <EwFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </EwDialog>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={EW_SECONDARY} aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={EW_PRIMARY} onClick={onClick} disabled={disabled}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function FormButtons({ pending, onCancel, label }: { pending: boolean; onCancel: () => void; label: string }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>
        Cancel
      </button>
      <button type="submit" className={EW_PRIMARY} disabled={pending}>
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ─── Crew ────────────────────────────────────────────────────────────────────

export function CrewSection({
  job,
  catalog,
  users,
  canWrite,
  showHours = false,
}: {
  job: EwJobDetail;
  catalog: EwCatalog | null;
  users: { id: string; name: string }[];
  canWrite: boolean;
  /** Office roles: each crew member links to their Employee Hours page. */
  showHours?: boolean;
}) {
  const add = useAddEwCrew();
  const remove = useRemoveEwCrew();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ user_id: '', crew_role: '', start_on: '', end_on: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwCrew | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onCrew = useMemo(() => new Set(job.crew.map((c) => c.userId)), [job.crew]);
  const pickable = users.filter((u) => !onCrew.has(u.id));

  const columns: EwColumn<EwCrew>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span> },
    { key: 'role', header: 'Crew role', render: (c) => c.crewRoleLabel || c.crewRole || '—' },
    { key: 'start', header: 'From', render: (c) => ewDay(c.startOn) },
    { key: 'end', header: 'To', render: (c) => ewDay(c.endOn) },
    { key: 'notes', header: 'Notes', render: (c) => c.notes || '—' },
    ...(showHours
      ? [{ key: 'hours', header: 'Hours', render: (c: EwCrew) => (
          <Link className="text-sm font-bold text-fi-amber hover:underline" to={`/earthworks/employees/${c.userId}/hours`} data-testid="ew-crew-hours-link">
            Employee hours
          </Link>
        ) }]
      : []),
    ...(canWrite
      ? [{ key: 'remove', header: '', align: 'right' as const, render: (c: EwCrew) => <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} /> }]
      : []),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.user_id) return setError('Choose who to add.');
    if (!form.crew_role) return setError('Choose their crew role.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          user_id: form.user_id,
          crew_role: form.crew_role,
          start_on: blankToNull(form.start_on),
          end_on: blankToNull(form.end_on),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Added to the crew');
          setAdding(false);
          setForm({ user_id: '', crew_role: '', start_on: '', end_on: '', notes: '' });
        },
        onError: (err) => setError(ewRefusal(err, 'They could not be added to the crew.')),
      },
    );
  };

  return (
    <EwPanel
      eyebrow="Crew"
      title="Who works this job"
      testId="ew-job-crew"
      actions={canWrite && !adding ? <AddButton label="Add crew" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-crew-form">
          <EwFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <EwField label="Person">
              <select className={EW_INPUT} aria-label="Person" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                <option value="">Choose…</option>
                {pickable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </EwField>
            <EwField label="Crew role">
              <select className={EW_INPUT} aria-label="Crew role" value={form.crew_role} onChange={(e) => setForm({ ...form, crew_role: e.target.value })}>
                <option value="">Choose…</option>
                {(catalog?.crewRoles ?? []).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </EwField>
            <EwField label="From">
              <input type="date" className={EW_DATE_INPUT} aria-label="Crew from" value={form.start_on} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
            </EwField>
            <EwField label="To">
              <input type="date" className={EW_DATE_INPUT} aria-label="Crew to" value={form.end_on} onChange={(e) => setForm({ ...form, end_on: e.target.value })} />
            </EwField>
            <EwField label="Notes">
              <input className={EW_INPUT} aria-label="Crew notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </EwField>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add to crew" />
        </form>
      )}
      <EwRegister
        query={SETTLED}
        rows={job.crew}
        columns={columns}
        rail={() => 'border-l-fi-steel'}
        subject="crew"
        testId="ew-crew"
        minWidth="min-w-[760px]"
        empty={<EwEmpty>No one is on this job's crew yet.</EwEmpty>}
        card={(c) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{c.userName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {c.crewRoleLabel || c.crewRole || '—'} · {ewDay(c.startOn)} → {ewDay(c.endOn)}
              </p>
              {showHours && (
                <Link className="mt-1 inline-block text-xs font-bold text-fi-amber hover:underline" to={`/earthworks/employees/${c.userId}/hours`} data-testid="ew-crew-hours-link-card">
                  Employee hours
                </Link>
              )}
            </div>
            {canWrite && <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove from crew"
        body={`Takes ${removing?.userName ?? 'this person'} off the crew.`}
        confirmLabel="Remove from crew"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Removed from the crew');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(ewRefusal(err, 'They could not be removed.')),
          })
        }
      />
    </EwPanel>
  );
}

// ─── Owned equipment ─────────────────────────────────────────────────────────

export function EquipmentSection({
  job,
  equipment,
  canWrite,
}: {
  job: EwJobDetail;
  equipment: { id: string; name: string; unitNumber: string | null; serialNumber: string | null }[];
  canWrite: boolean;
}) {
  const add = useAddEwJobEquipment();
  const remove = useRemoveEwJobEquipment();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ equipment_id: '', start_on: '', end_on: '', notes: '' });
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwJobEquipment | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onJob = useMemo(() => new Set(job.equipment.map((e) => e.equipmentId)), [job.equipment]);
  const pickable = equipment.filter((e) => !onJob.has(e.id));
  const name = (e: EwJobEquipment) => e.equipmentName || e.unitNumber || 'this unit';

  const columns: EwColumn<EwJobEquipment>[] = [
    { key: 'unit', header: 'Unit', render: (e) => <span className="font-extrabold text-white">{e.equipmentName || '—'}</span> },
    { key: 'number', header: 'Unit #', render: (e) => <span className="font-mono">{e.unitNumber || '—'}</span> },
    { key: 'serial', header: 'Serial', render: (e) => <span className="font-mono">{e.serialNumber || '—'}</span> },
    { key: 'type', header: 'Type', render: (e) => e.equipmentType || '—' },
    { key: 'start', header: 'From', render: (e) => ewDay(e.startOn) },
    { key: 'end', header: 'To', render: (e) => ewDay(e.endOn) },
    ...(canWrite
      ? [{ key: 'remove', header: '', align: 'right' as const, render: (e: EwJobEquipment) => <RemoveButton label={`Remove ${name(e)}`} onClick={() => { setRemoveError(null); setRemoving(e); }} /> }]
      : []),
  ];

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!form.equipment_id) return setError('Choose the unit.');
    add.mutate(
      {
        jobId: job.id,
        body: { equipment_id: form.equipment_id, start_on: blankToNull(form.start_on), end_on: blankToNull(form.end_on), notes: blankToNull(form.notes) },
      },
      {
        onSuccess: () => {
          toast.success('Unit added to the job');
          setAdding(false);
          setForm({ equipment_id: '', start_on: '', end_on: '', notes: '' });
        },
        onError: (err) => setError(ewRefusal(err, 'The unit could not be added.')),
      },
    );
  };

  return (
    <EwPanel
      eyebrow="Equipment"
      title="Owned units on this job"
      testId="ew-job-equipment"
      actions={canWrite && !adding ? <AddButton label="Add unit" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-equipment-form">
          <EwFormError message={error} />
          {pickable.length === 0 ? (
            <EwEmpty>Every unit in your equipment list is already on this job, or the list is empty. Add units under Equipment.</EwEmpty>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <EwField label="Unit">
                <select className={EW_INPUT} aria-label="Unit" value={form.equipment_id} onChange={(e) => setForm({ ...form, equipment_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {pickable.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                      {e.unitNumber ? ` · ${e.unitNumber}` : ''}
                    </option>
                  ))}
                </select>
              </EwField>
              <EwField label="From">
                <input type="date" className={EW_DATE_INPUT} aria-label="Unit from" value={form.start_on} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
              </EwField>
              <EwField label="To">
                <input type="date" className={EW_DATE_INPUT} aria-label="Unit to" value={form.end_on} onChange={(e) => setForm({ ...form, end_on: e.target.value })} />
              </EwField>
              <EwField label="Notes">
                <input className={EW_INPUT} aria-label="Unit notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </EwField>
            </div>
          )}
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add unit" />
        </form>
      )}
      <EwRegister
        query={SETTLED}
        rows={job.equipment}
        columns={columns}
        rail={() => 'border-l-fi-steel'}
        subject="equipment"
        testId="ew-job-unit"
        minWidth="min-w-[820px]"
        empty={<EwEmpty>No owned unit is on this job yet.</EwEmpty>}
        card={(e) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{e.equipmentName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {e.unitNumber || '—'} · {e.equipmentType || '—'}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${name(e)}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove unit"
        body={`Takes ${removing ? name(removing) : 'this unit'} off the job.`}
        confirmLabel="Remove unit"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Unit removed from the job');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(ewRefusal(err, 'The unit could not be removed.')),
          })
        }
      />
    </EwPanel>
  );
}

// ─── Rent-ins ────────────────────────────────────────────────────────────────
